import { db } from './firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot, 
  query, 
  orderBy,
  getDocs,
  setDoc
} from 'firebase/firestore';

export class SupplementsService {
  constructor(foodTruckId) {
    if (!foodTruckId) {
      throw new Error('Food truck ID is required');
    }
    this.collectionPath = `foodTrucks/${foodTruckId}/supplements`;
  }

  /**
   * Ajoute un nouveau supplément
   * @param {Object} supplementData - Les données du supplément
   * @returns {Promise<string>} L'ID du nouveau supplément
   */
  async addSupplement(supplementData) {
    try {
      const docRef = await addDoc(collection(db, this.collectionPath), supplementData);
      const supplementWithId = {
        id: docRef.id,
        ...supplementData
      };
      await setDoc(docRef, supplementWithId);
      return docRef.id;
    } catch (error) {
      console.error("Error adding supplement:", error);
      throw error;
    }
  }

  /**
   * Met à jour un supplément existant
   * @param {Object} supplementData - Les données du supplément avec son ID
   */
  async updateSupplement(supplementData) {
    const { id, ...updateData } = supplementData;
    try {
      await updateDoc(doc(db, this.collectionPath, id), updateData);
    } catch (error) {
      console.error("Error updating supplement:", error);
      throw error;
    }
  }

  /**
   * Supprime un supplément
   * @param {string} supplementId - L'ID du supplément à supprimer
   */
  async deleteSupplement(supplementId) {
    try {
      await deleteDoc(doc(db, this.collectionPath, supplementId));
    } catch (error) {
      console.error("Error deleting supplement:", error);
      throw error;
    }
  }

  /**
   * Récupère tous les suppléments
   * @returns {Promise<Array>} Liste des suppléments
   */
  async getSupplements() {
    try {
      const supplementsQuery = query(
        collection(db, this.collectionPath), 
        orderBy('name')
      );
      const querySnapshot = await getDocs(supplementsQuery);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error fetching supplements:", error);
      throw error;
    }
  }

  /**
   * Écoute les changements des suppléments en temps réel
   * @param {Function} onUpdate - Callback appelé lors des mises à jour
   * @param {Function} onError - Callback appelé en cas d'erreur
   * @returns {Function} Fonction pour arrêter l'écoute
   */
  listenSupplements(onUpdate, onError) {
    const q = query(collection(db, this.collectionPath), orderBy('name'));
    return onSnapshot(q, 
      (querySnapshot) => {
        const supplements = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        onUpdate(supplements);
      },
      (error) => {
        console.error("Error listening to supplements:", error);
        onError(error);
      }
    );
  }
}

// Export singleton instance factory
export const supplementsService = {
  /**
   * Crée ou récupère une instance du service pour un food truck spécifique
   * @param {string} foodTruckId - ID du food truck
   * @returns {SupplementsService}
   */
  getInstance(foodTruckId) {
    if (!foodTruckId) {
      throw new Error('Food truck ID is required');
    }
    return new SupplementsService(foodTruckId);
  }
};